import { z } from 'zod';
import { INDICATOR_KINDS, type Thresholds } from './devices.ts';
import { DATA_RANGES } from './pages.ts';

/**
 * Measurement history ("O'lchovlar tarixi") and calibration formulas.
 *
 * History lives in the Constructor's own `indicator_readings` table (the unified server keeps only the latest value).
 * Physical indicators are stored downsampled (≤ 1 reading per minute); virtual indicators are computed on read by
 * applying their formula (`x` = value of `sourceIndicatorId`) to the source history.
 */

/** Aggregation step. `auto` picks the smallest step that keeps ≤ `maxPoints` points; `raw` returns stored readings. */
export const HISTORY_BUCKETS = [
  'auto',
  'raw',
  '1m',
  '5m',
  '15m',
  '30m',
  '1h',
  '3h',
  '6h',
  '12h',
  '1d',
  '7d',
] as const;
export type HistoryBucket = (typeof HISTORY_BUCKETS)[number];
/** The step actually used (never `auto`; `raw` = no aggregation). */
export type EffectiveBucket = Exclude<HistoryBucket, 'auto'>;

/** ISO 8601 date-time with offset (`2026-10-27T14:30:00+05:00`, `…Z`) or a date (`2026-10-27`, Asia/Tashkent day). */
export const historyInstantSchema = z.union([z.iso.datetime({ offset: true }), z.iso.date()]);

const historyRangeFields = {
  /** inclusive start; default = `to` − 24 h (or derived from `range`) */
  from: historyInstantSchema.optional(),
  /** exclusive end; default = now. A plain date means the end of that day. */
  to: historyInstantSchema.optional(),
  /** shortcut for the period chips ("1 KUN", "7 KUN", "30 KUN", "Barchasi"); `from`/`to` win when given */
  range: z.enum(DATA_RANGES).optional(),
  bucket: z.enum(HISTORY_BUCKETS).default('auto'),
  /** upper bound of points per series (explicit buckets are coarsened to respect it) */
  maxPoints: z.coerce.number().int().min(10).max(5000).default(500),
};

/** `GET /tenants/:t/sensors/:id/history` */
export const sensorHistoryQuerySchema = z.object(historyRangeFields);
export type SensorHistoryQuery = z.infer<typeof sensorHistoryQuerySchema>;

const idList = (max: number) =>
  z
    .string()
    .transform((s) =>
      s
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean),
    )
    .pipe(z.array(z.uuid()).min(1).max(max));

/** `GET /tenants/:t/readings?ids=a,b` (also `GET /tenants/:t/series?indicatorIds=a,b`) — multi-series for charts and tables. */
export const readingsQuerySchema = z.object({ ...historyRangeFields, ids: idList(50) });
export type ReadingsQuery = z.infer<typeof readingsQuerySchema>;

/** Indicator summary embedded in series, alarm events and charts. */
export interface IndicatorRefDto {
  id: string;
  name: string;
  code: string;
  unit: string | null;
  precision: number;
  kind: (typeof INDICATOR_KINDS)[number];
  color: string | null;
  chartType: string;
  thresholds: Thresholds | null;
  sourceIndicatorId: string | null;
  formula: string | null;
  deviceId: string;
  deviceCode: string;
  deviceName: string;
  /** device category ("Qurilma turkumi") — groups of the chart tree */
  categoryId: string | null;
  categoryName: string | null;
}

/** One bucket; `value` = average (raw reading for `raw`), `min`/`max` = extremes inside the bucket. */
export interface HistoryPoint {
  ts: string;
  value: number | null;
  min: number | null;
  max: number | null;
  count: number;
}

/** Statistics over the requested range (KPI cards: last value, "Min … Max …", "54 ta o'lchov"). */
export interface SeriesStats {
  min: number | null;
  max: number | null;
  avg: number | null;
  last: number | null;
  lastAt: string | null;
  count: number;
}

export interface IndicatorSeries {
  indicatorId: string;
  indicator: IndicatorRefDto;
  points: HistoryPoint[];
  stats: SeriesStats;
}

interface SeriesWindow {
  from: string;
  to: string;
  bucket: EffectiveBucket;
  /** bucket length in seconds; null for `raw` */
  bucketSeconds: number | null;
}

export interface SensorHistoryResponse extends SeriesWindow, IndicatorSeries {}

/** All series share one bucket grid, so table mode can join rows by `ts`. */
export interface ReadingsResponse extends SeriesWindow {
  series: IndicatorSeries[];
  /** requested ids that are unknown, deleted or belong to another tenant (e.g. a deleted indicator of a saved chart) */
  missingIds: string[];
}

/* ───────────── calibration formulas ───────────── */

/**
 * Formula grammar (virtual indicators, "Qurilma kolibrovkalash"):
 *  - variable `x` (= value of the source indicator), numbers with a decimal point or comma (`0,0001`), `pi`;
 *  - `+ - * / ^` (also `− × · ÷ **`), superscripts `x² x³`, parentheses, unary minus, implicit multiplication
 *    (`2x`, `2(x+1)`, `x(x+1)`, `(a)(b)`);
 *  - functions `abs sqrt min max pow log exp round` (+ `ln`, `log10`); `log(a)` is natural, `log(a; b)` base b;
 *    `round(a; digits)`. Arguments are separated by `;` — or by `,` when no `;` is used (then `max(1,5)` = two args).
 */
export const FORMULA_FUNCTIONS = [
  'abs',
  'sqrt',
  'min',
  'max',
  'pow',
  'log',
  'ln',
  'log10',
  'exp',
  'round',
] as const;

export const formulaPreviewSchema = z.object({
  formula: z.string().max(4000),
  /** value of `x`; a string may use a decimal comma */
  x: z
    .union([
      z.number(),
      z
        .string()
        .trim()
        .regex(/^[-+]?\d+(?:[.,]\d+)?(?:[eE][-+]?\d+)?$/, "x son bo'lishi kerak"),
    ])
    .transform((v) => (typeof v === 'number' ? v : Number(v.replace(',', '.'))))
    .pipe(z.number().finite())
    .nullish(),
  /** take `x` from the latest value of this indicator (the "=24" live preview) */
  sourceIndicatorId: z.uuid().nullish(),
});
export type FormulaPreviewInput = z.input<typeof formulaPreviewSchema>;

export const FORMULA_ERROR_CODES = [
  'empty',
  'too_long',
  'unexpected_char',
  'unexpected_token',
  'unexpected_end',
  'missing_paren',
  'unknown_identifier',
  'arity',
  'too_deep',
  'division_by_zero',
  'domain',
  'not_finite',
] as const;
export type FormulaErrorCode = (typeof FORMULA_ERROR_CODES)[number];

/** A formula problem with the offending span `[position, end)` (UTF-16 indexes into the submitted text). */
export interface FormulaErrorDto {
  code: FormulaErrorCode;
  /** Uzbek message; the UI shows it as "Formula xato: …" */
  message: string;
  position: number;
  end: number;
}

export type FormulaPreviewResponse =
  | { valid: true; value: number | null; x: number | null }
  | { valid: false; error: FormulaErrorDto; x: number | null };
